import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { authRepository } from '@/data/adapters/api/auth.api'
import { ensureWorkspaces, refetchWorkspaces } from '@/lib/api/resolve-workspaces'
import { requireAuth } from '@/lib/auth-guard'
import { shouldGateSelfServe } from '@/stores/self-serve.store'
import { resolveOrgAccess, type OrgAccess } from '@/lib/org-access'
import type { Organization, User } from '@/modules/shared/types'

interface OrgContext {
  org: Organization
  currentUser: User
  orgAccess: OrgAccess
}

export const Route = createFileRoute('/$orgSlug')({
  beforeLoad: async ({ params, context, location }) => {
    const { user: currentUser } = await requireAuth()

    // Cache-first: hits the 5-min query cache instead of refetching on every
    // navigation into this subtree.
    const { queryClient } = context as { queryClient: QueryClient }
    const workspaces = await ensureWorkspaces(queryClient)

    let resolution = resolveOrgAccess(workspaces, params.orgSlug, location.pathname)

    if (resolution.kind === 'not-found') {
      // A miss may just be a stale cache (org/store granted after the cache
      // was filled, in another tab, or by a teammate) — force one
      // authoritative read before giving up. See `resolve-workspaces.ts`.
      const fresh = await refetchWorkspaces(queryClient)
      resolution = resolveOrgAccess(fresh, params.orgSlug, location.pathname)
    }

    if (resolution.kind === 'not-found') throw notFound()

    if (resolution.kind === 'member') {
      const { org } = resolution
      // Self-serve workspaces are dashboard-gated until SCC activates them: the
      // only reachable pages are their onboarding (connect) routes.
      if (shouldGateSelfServe(org.id, location.pathname)) {
        const clients = await queryClient.ensureQueryData({
          queryKey: ['auth', 'clients', org.id],
          queryFn: () => authRepository.getClients(org.id),
        })
        const first = clients[0]
        if (first) {
          throw redirect({
            to: '/$orgSlug/onboarding/$clientSlug',
            params: { orgSlug: org.slug, clientSlug: first.slug },
            search: { connect: true },
          })
        }
      }
      return { org, currentUser, orgAccess: 'member' as const } satisfies OrgContext
    }

    // store-only — a direct store membership in this agency, never agency pages.
    if (resolution.redirectToClientSlug) {
      throw redirect({
        to: '/$orgSlug/$clientSlug',
        params: { orgSlug: params.orgSlug, clientSlug: resolution.redirectToClientSlug },
      })
    }

    return {
      org: resolution.org,
      currentUser,
      orgAccess: 'store-only' as const,
    } satisfies OrgContext
  },
})
